import QuestionMarkOutline from '@simplywallst/ui-core/icons/QuestionMarkOutline';
import { Tab, TabList, useTheme } from '@simplywallst/ui-core';
import { Bones } from '@simplywallst/ui-core';
import { MinimalDropdown } from '@/components/Dropdown';
import Tooltip from '@/components/Tooltip';
import If from '@/components/If';
import useTitleGenerator from '../../hooks/useTitleGenerator';
import type { PeriodType, ChartData } from '../../hooks/useMarketPerformance';
import {
  ChartHeader,
  PETooltip,
  Tip,
  ToolTipText,
  TopChartHeader,
  ValuationSelection,
} from './styled';
import type { ValuationType } from './MarketPerformanceChart';
import { PETooltipWidth, ValuationOptions } from './MarketPerformanceChart';

const TooltipData: Record<ValuationType, string> = {
  ps: 'The price-to-sales ratio is generally used as a substitute for the price-to-earnings ratio for companies that are not consistently profitable. This is calculated by dividing the total market cap for companies in the [market or industry] by the total revenue.',
  absolutePE:
    'The price-to-earnings ratio is generally used as an indicator of relative value between companies that are generally mature and profitable. This is calculated by dividing the total market cap for companies in the [market or industry] by the total earnings.',
  medianPE:
    'The price-to-earnings ratio is generally used as an indicator of relative value between companies that are mature and profitable. This is calculated by taking the median PE value from all profitable companies in the [market or industry].',
};

interface Props {
  isLoading: boolean;
  xPoint: number;
  width: number;
  period: PeriodType;
  valuationType: ValuationType;
  datum: ChartData[number];
  verticalPadding: number;
  handlePeriodSelect: (period: string | number) => void;
  handleValuationTypeSelect: (valuationType: string) => void;
  valuationLabel: string;
}

const PerformanceChartHeader = ({
  isLoading,
  xPoint,
  width,
  period,
  valuationType,
  datum,
  verticalPadding,
  handlePeriodSelect,
  handleValuationTypeSelect,
  valuationLabel,
}: Props) => {
  const theme = useTheme();
  const realMarketIndustry = useTitleGenerator({
    marketTemplate: `[Market] market`,
    sectorTemplate: `[Market] [Sector] sector`,
    industryTemplate: `[Market] [Industry] industry`,
  });

  const peTooltipTransformX = () => {
    const x = xPoint || 0;
    if (x < 60) {
      return verticalPadding;
    }
    if (x >= 60 && x < width - PETooltipWidth) {
      return x - verticalPadding;
    }
    return width - PETooltipWidth - verticalPadding;
  };
  return (
    <ChartHeader>
      <If match={isLoading}>
        <Bones minWidth="260px" minHeight="30px" />
      </If>
      <If match={!isLoading}>
        <TopChartHeader
          templateColumns="minmax(min-content, 280px) max-content"
          justifyContent="space-between"
          alignItems="center"
        >
          <TabList templateColumns="repeat(4, auto)">
            {['1Y', '3Y', '5Y', '10Y'].map((p) => (
              <Tab
                key={p}
                value={p}
                styleType="period"
                selected={period === p}
                onClick={() => handlePeriodSelect(p)}
              >
                {p}
              </Tab>
            ))}
          </TabList>
          <ValuationSelection justifyContent="flex-end">
            <MinimalDropdown
              options={ValuationOptions}
              value={valuationType}
              onSelect={handleValuationTypeSelect}
              label={valuationLabel}
              width={200}
              align="right"
            />
          </ValuationSelection>
        </TopChartHeader>
        <PETooltip x={peTooltipTransformX()}>
          <span>
            {valuationLabel} Ratio{' '}
            <Tooltip
              title={
                <ToolTipText>
                  {TooltipData[valuationType].replace(
                    '[market or industry]',
                    realMarketIndustry
                  )}
                </ToolTipText>
              }
              light
              zIndex={99999}
            >
              <QuestionMarkOutline
                width={24}
                height={24}
                fill={theme.color.charcoalRGBA(0.5)}
              />
            </Tooltip>
          </span>
          <p data-cy-id="market-valuation-chart-pe">
            {datum[`${valuationType}Formatted`]}
          </p>
          <br />
          <time dateTime={datum.dateFormatted}>{datum.dateFormatted}</time>
        </PETooltip>
        <Tip x={xPoint} flip={xPoint < width / 2} />
      </If>
    </ChartHeader>
  );
};

export default PerformanceChartHeader;
